import React, { useState, useEffect } from 'react';
import api from '../api';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import SkillsInput from '../components/SkillsInput';
import DomainSelector from '../components/DomainSelector';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import './Register.css';
import { INDUSTRY_DOMAINS, POP_BACKGROUND_CATEGORIES, DOMAIN_SKILLS } from '../constants/domains';
import { EXPERTISE_TAXONOMY } from '../constants/expertise';
import Autocomplete from '../components/Autocomplete';
import { ALL_LOCATIONS } from '../constants/locations';
import { EDUCATION_OPTIONS, BRANCH_OPTIONS, TECHNICAL_DEGREES } from '../constants/education';
import registerImg from '../assets/register.png';
import roleBg from '../assets/role_bg.png';
import { Building2, Briefcase, Landmark, GraduationCap } from 'lucide-react';

function Register() {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState('account');
  const [selectedRole, setSelectedRole] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [showRoleDetails, setShowRoleDetails] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [universities, setUniversities] = useState([]);
  const navigate = useNavigate();
  const [touched, setTouched] = useState({});

  const getEmailError = () => {
    if (!touched.email || !formData.email) return '';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) return 'Please enter a valid email address';
    return '';
  };

  const getPasswordError = () => {
    if (!touched.password || !formData.password) return '';
    if (formData.password.length < 8) {
      return `Password must be at least 8 characters (${formData.password.length}/8)`;
    }
    if (formData.password.length > 64) {
      return `Password can contain up to 64 characters (${formData.password.length}/64)`;
    }
    return '';
  };

  const getConfirmPasswordError = () => {
    if (!touched.confirmPassword || !formData.confirmPassword) return '';
    if (formData.confirmPassword !== formData.password) {
      return 'Passwords do not match';
    }
    return '';
  };

  // Toast notification state
  const [toasts, setToasts] = useState([]);

  // Toast notification helper
  const showToast = (message, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    }, 4000);
  };


  const [formData, setFormData] = useState({
    email: '', password: '', confirmPassword: '', phone_number: '',
    organization_name: '', institution_type: '', city: '', state: '', country: '', contact_person: '',
    contact_honorific: 'Dr.', custom_contact_honorific: '',
    full_name: '', honorific: 'Dr.', custom_honorific: '', job_title: '', company: '', location: '', domain: INDUSTRY_DOMAINS[0], background_category: POP_BACKGROUND_CATEGORIES[0], years_of_experience: '',
    highest_education: '',
    education_specialization: '',
    custom_education: '',
    custom_specialization: '',
    linkedin_url: '', resume_link: '',
    student_institution: '', degree: '', graduation_year: '',
    skills: [],
    preferred_domains: [],
    primary_domain: null,
    oauth_provider: null,
    tier: '', // No Default
    expertise_category: '',
    expertise_subcategory: '',
    expertise_core: ''
  });

  // Fetch universities on component mount
  useEffect(() => {
    const fetchUniversities = async () => {
      try {
        const response = await api.get('/public/universities');
        setUniversities(response.data || []);
      } catch (err) {
        console.error('Failed to fetch universities:', err);
      }
    };
    fetchUniversities();
  }, []);

  // Dynamic Taxonomy Lists
  const [dynIndustries, setDynIndustries] = useState([]);
  const [dynCategories, setDynCategories] = useState([]);
  const [dynSubcategories, setDynSubcategories] = useState([]);

  useEffect(() => {
    api.get('/expertise-categories').then(res => {
      setDynIndustries(res.data || []);
    }).catch(err => console.error("Could not load dynamic categories:", err));
  }, []);

  useEffect(() => {
    if (!formData.expertise_category) {
      setDynCategories([]);
      return;
    }
    const ind = dynIndustries.find(i => i.name === formData.expertise_category);
    if (ind) {
      api.get(`/expertise-subcategories?categoryId=${ind.id}`).then(res => {
        setDynCategories(res.data || []);
      }).catch(err => console.error(err));
    } else {
      setDynCategories([]);
    }
  }, [formData.expertise_category, dynIndustries]);

  useEffect(() => {
    if (!formData.expertise_subcategory) {
      setDynSubcategories([]);
      return;
    }
    const cat = dynCategories.find(c => c.name === formData.expertise_subcategory);
    if (cat) {
      api.get(`/core-expertise?subcategoryId=${cat.id}`).then(res => {
        setDynSubcategories(res.data || []);
      }).catch(err => console.error(err));
    } else {
      setDynSubcategories([]);
    }
  }, [formData.expertise_subcategory, dynCategories]);

  // Handle OAuth users returning from Google
  useEffect(() => {
    const isOAuthFlow = searchParams.get('oauth') === 'google';
    if (isOAuthFlow) {
      const googleUserData = sessionStorage.getItem('google_user');
      if (googleUserData) {
        const userData = JSON.parse(googleUserData);
        setFormData(prev => ({
          ...prev,
          email: userData.email,
          full_name: userData.name || '',
          oauth_provider: 'google'
        }));
        setStep('selection');
        setSuccess('Email verified via Google! Please select your role.');
        sessionStorage.removeItem('google_user'); // Clean up
      }
    }
  }, [searchParams]);

   const handleChange = (e) => {
    let { name, value } = e.target;
    if (name === 'years_of_experience') {
      if (value !== '') {
        const parsed = parseInt(value, 10);
        if (isNaN(parsed) || parsed < 0) {
          value = '0';
        } else if (parsed > 70) {
          value = '70';
        } else {
          value = String(parsed);
        }
      }
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (value) => {
    setFormData(prev => ({ ...prev, phone_number: value }));
  };
  const handleSkillsChange = (newSkills) => setFormData({ ...formData, skills: newSkills });
  const handleDomainsChange = (newDomains) => {
    setFormData({
      ...formData,
      preferred_domains: newDomains,
      primary_domain: newDomains.length > 0 ? newDomains[0] : null
    });
  };

  // Google OAuth
  const handleGoogleSignup = async () => {
    try {
      const response = await api.get('/auth/google?context=register');
      window.location.href = response.data.auth_url;
    } catch (err) {
      setError('Failed to initiate Google signup');
    }
  };

  // Request OTP
  const handleRequestOTP = async () => {
    setTouched({ email: true, password: true, confirmPassword: true });
    
    if (!formData.email) {
      setError('Please enter your email first');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }
    if (formData.password.length > 64) {
      setError('Password can contain up to 64 characters');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setError('');
    setLoading(true);
    console.log("REGISTER: Attempting to request OTP for:", formData.email);
    try {
      const resp = await api.post('/request-otp', { email: formData.email, purpose: 'register' });
      console.log("REGISTER: OTP Request Success:", resp.data);
      setOtpSent(true);
      setSuccess('Verification code sent to your email!');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  // Verify OTP
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.post('/verify-registration-otp', {
        email: formData.email,
        otp: otp,
        purpose: 'register'
      });
      setSuccess('Email verified! Select your role to continue.');
      setStep('selection');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  // Validation Helper
  const validateForm = (data, role) => {
    const nameRegex = /^[a-zA-Z\s'.-]+$/;
    // Simple URL regex: Optional protocol, Domain (chars/digits/hyphens/dots), TLD (chars >= 2), Optional path
    const urlRegex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/i;

    // 0. Required Fields Check (Post-trim)
    if (!data.phone_number) return "Phone number is required.";
    if (!/^[\d+]{10,20}$/.test(data.phone_number)) return "Phone number must contain only numbers (minimum 10 digits).";
    
    if (role === 'student') {
      if (!data.full_name) return "Full Name is required.";
      if (!data.student_institution) return "Institution is required.";
    } else if (role === 'pop') {
      if (!data.full_name) return "Full Name is required.";
      if (data.honorific === 'Other' && !data.custom_honorific) return "Please enter a custom honorific.";
      if (data.honorific === 'Other' && !/^[a-zA-Z.\s]+$/.test(data.custom_honorific)) return "Custom honorific contains invalid characters.";
      if (!data.job_title) return "Job Title is required.";
      if (!data.company) return "Company is required.";
      if (!data.location) return "Location is required.";
      if (!data.domain) return "Primary Domain is required.";
      if (!data.background_category) return "Background Category is required.";
      if (!data.expertise_category) return "Expertise Category is required.";
      if (!data.expertise_subcategory) return "Expertise Sub-category is required.";
      if (!data.expertise_core) return "Expertise Core is required.";
      if (data.years_of_experience === '' || data.years_of_experience === null || data.years_of_experience === undefined) {
        return "Years of Experience is required.";
      }
      const expNum = Number(data.years_of_experience);
      if (isNaN(expNum) || expNum < 0) {
        return "Years of Experience must be 0 or greater.";
      }
      if (expNum > 70) {
        return "Years of Experience cannot exceed 70 years.";
      }
      if (!data.highest_education) return "Highest Education is required.";
    } else if (role === 'university') {
      if (!data.organization_name) return "Organization Name is required.";
      if (!data.institution_type) return "Institution Type is required.";
      if (!data.city) return "Location is required.";
      if (!data.contact_person) return "Contact Person is required.";
      if (data.contact_honorific === 'Other' && !data.custom_contact_honorific) return "Please enter a custom honorific for the contact person.";
      if (data.contact_honorific === 'Other' && !/^[a-zA-Z.\s]+$/.test(data.custom_contact_honorific)) return "Custom honorific contains invalid characters.";
      if (!data.tier) return "Please select a University Tier.";
    }

    // 1. Common Validations
    if (role !== 'university' && data.full_name) {
      if (!nameRegex.test(data.full_name)) return "Name can only contain letters, spaces, hyphens, and apostrophes.";
      if (data.full_name.length < 2) return "Name must be at least 2 characters long.";
    }

    // 2. Role Specific
    if (role === 'student') {
      // No specific validation needed anymore
    } else if (role === 'pop') {
      if (data.linkedin_url && !data.linkedin_url.includes('linkedin.com')) return "Please enter a valid LinkedIn URL.";
      if (data.resume_link && !urlRegex.test(data.resume_link)) return "Please enter a valid Resume/Portfolio link.";
    } else if (role === 'university') {
      if (!nameRegex.test(data.contact_person)) return "Contact person name contains invalid characters.";
    }

    return null; // No error
  };

  const handleFinalSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // 1. Trim Data
    const trimmedData = Object.keys(formData).reduce((acc, key) => {
      acc[key] = typeof formData[key] === 'string' ? formData[key].trim() : formData[key];
      return acc;
    }, {});

    // 2. Validate
    const validationError = validateForm(trimmedData, selectedRole);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      const skillsString = Array.isArray(trimmedData.skills) ? trimmedData.skills.join(', ') : '';
      const payload = {
        email: trimmedData.email,
        password: trimmedData.password,
        role: selectedRole,
        oauth_provider: trimmedData.oauth_provider,
        phone_number: trimmedData.phone_number,
        ...(selectedRole === 'university' ? (() => {
          const parts = (trimmedData.city || '').split(', ');
          const effectiveHon = (trimmedData.contact_honorific === 'Other' ? (trimmedData.custom_contact_honorific || '') : (trimmedData.contact_honorific || 'Dr.')).trim();
          const rawContact = trimmedData.contact_person || '';
          const combinedContact = effectiveHon && !rawContact.toLowerCase().startsWith(effectiveHon.toLowerCase())
            ? `${effectiveHon} ${rawContact}`.trim()
            : rawContact;

          return {
            organization_name: trimmedData.organization_name,
            institution_type: trimmedData.institution_type,
            city: parts[0] || '',
            state: parts[1] || '',
            country: parts[2] || 'India',
            contact_person: combinedContact,
            contact_honorific: trimmedData.contact_honorific,
            custom_contact_honorific: trimmedData.custom_contact_honorific,
            tier: trimmedData.tier
          };
        })() : selectedRole === 'pop' ? (() => {
          const effectiveHon = (trimmedData.honorific === 'Other' ? (trimmedData.custom_honorific || '') : (trimmedData.honorific || 'Dr.')).trim();
          const rawName = trimmedData.full_name || '';
          const combinedName = effectiveHon && !rawName.toLowerCase().startsWith(effectiveHon.toLowerCase())
            ? `${effectiveHon} ${rawName}`.trim()
            : rawName;

          return {
            full_name: combinedName,
            honorific: trimmedData.honorific,
            custom_honorific: trimmedData.custom_honorific,
            job_title: trimmedData.job_title,
            company: trimmedData.company,
            // Extract city/state/country from the location string
            ...(trimmedData.location ? (() => {
              const parts = trimmedData.location.split(', ');
              return {
                city: parts[0] || '',
                state: parts[1] || '',
                country: parts[2] || 'India',
                location: trimmedData.location
              };
            })() : {}),
            expertise_areas: skillsString,
            domain: trimmedData.domain,
            background_category: trimmedData.background_category,
            expertise_category: trimmedData.expertise_category,
            expertise_subcategory: trimmedData.expertise_subcategory,
            expertise_core: trimmedData.expertise_core,
            years_of_experience: parseInt(trimmedData.years_of_experience),
            highest_education: trimmedData.highest_education,
            education_specialization: trimmedData.education_specialization,
            custom_education: trimmedData.custom_education,
            custom_specialization: trimmedData.custom_specialization,
            preferred_locations: trimmedData.location ? [trimmedData.location] : [],
            linkedin_url: trimmedData.linkedin_url,
            resume_link: trimmedData.resume_link
          };
        })() : {
          full_name: formData.full_name,
          student_institution: formData.student_institution,
          university_name: formData.student_institution,
          student_skills: skillsString,
          preferred_domains: formData.preferred_domains,
          primary_domain: formData.primary_domain
        })
      };

      const response = await api.post('/register', payload);
      localStorage.removeItem('EdVergenceX_reg_state'); // Clear saved state
      sessionStorage.removeItem('google_user');

      if (response.data.access_token) {
        localStorage.setItem('token', response.data.access_token);
        if (response.data.role) localStorage.setItem('role', response.data.role);
        if (response.data.accessible_modules) localStorage.setItem('accessible_modules', JSON.stringify(response.data.accessible_modules));
        if (response.data.user) localStorage.setItem('user', JSON.stringify(response.data.user));

        showToast('Registration Successful! Redirecting to dashboard...', 'success');

        const role = (response.data.role || selectedRole).toLowerCase();
        const baseRole = (response.data.base_role || '').toLowerCase();

        setTimeout(() => {
          if (role === 'university' || baseRole === 'university') navigate('/uni-dashboard');
          else if (role === 'pop' || baseRole === 'pop') navigate('/pop-dashboard');
          else if (role === 'admin' || baseRole === 'admin') navigate('/admin-dashboard');
          else if (role === 'student' || baseRole === 'student') navigate('/student-dashboard');
          else if (role === 'faculty' || baseRole === 'faculty') navigate('/faculty-dashboard');
          else if (role === 'tpo' || baseRole === 'tpo') navigate('/tpo-dashboard');
          else navigate('/login');
        }, 1500);
      } else {
        showToast('Registration Successful! Account pending approval. Redirecting to login...', 'success');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
    }
  };

  // --- STEP 1: Account Creation with OTP Verification ---
  if (step === 'account') {
    return (
      <div className="register-wrapper">
        <div className="register-split">
          {/* Left image side */}
          <div className="register-image-side">
            <img src={registerImg} alt="Join EdVergenceX" />
            <div className="image-overlay">
              <h2>Join EdVergenceX Today</h2>
              <p>Become part of a thriving ecosystem where industry meets academia.</p>
            </div>
          </div>

          {/* Right form side */}
          <div className="register-form-side">
            <div className="auth-card">
              <h1>Get Started</h1>
              <p>Create your account to access the platform</p>

              <div className="social-login">
                <button className="social-btn" onClick={handleGoogleSignup}>
                  <img src="https://cdn-icons-png.flaticon.com/512/2991/2991148.png" alt="" />
                  Sign up with Google
                </button>
              </div>

              <div className="divider"><span>OR CONTINUE WITH EMAIL</span></div>

              {error && <div className="error-message">{error}</div>}
              {success && <div className="success-message">{success}</div>}

              {!otpSent ? (
                // Email & Password Form
                <form onSubmit={(e) => { e.preventDefault(); handleRequestOTP(); }}>
                  <div className="form-field">
                    <label>Email Address</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => {
                        handleChange(e);
                        setTouched(prev => ({ ...prev, email: true }));
                      }}
                      onBlur={() => setTouched(prev => ({ ...prev, email: true }))}
                      required
                      placeholder="you@example.com"
                      className={getEmailError() ? 'input-invalid' : formData.email && touched.email ? 'input-valid' : ''}
                    />
                    {getEmailError() && <span className="field-error-text">⚠️ {getEmailError()}</span>}
                  </div>

                  <div className="form-field">
                    <label>Password</label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={(e) => {
                        handleChange(e);
                        setTouched(prev => ({ ...prev, password: true }));
                      }}
                      onBlur={() => setTouched(prev => ({ ...prev, password: true }))}
                      required
                      placeholder="••••••••"
                      className={getPasswordError() ? 'input-invalid' : formData.password && touched.password && !getPasswordError() ? 'input-valid' : ''}
                    />
                    {getPasswordError() ? (
                      <span className="field-error-text">⚠️ {getPasswordError()}</span>
                    ) : (
                      <span className="password-hint">
                        Password must be 8–64 characters long. Longer passphrases are recommended for better security.
                      </span>
                    )}
                  </div>

                  <div className="form-field">
                    <label>Confirm Password</label>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={(e) => {
                        handleChange(e);
                        setTouched(prev => ({ ...prev, confirmPassword: true }));
                      }}
                      onBlur={() => setTouched(prev => ({ ...prev, confirmPassword: true }))}
                      required
                      placeholder="••••••••"
                      className={getConfirmPasswordError() ? 'input-invalid' : formData.confirmPassword && touched.confirmPassword && !getConfirmPasswordError() ? 'input-valid' : ''}
                    />
                    {getConfirmPasswordError() ? (
                      <span className="field-error-text">⚠️ {getConfirmPasswordError()}</span>
                    ) : touched.confirmPassword && formData.confirmPassword && !getConfirmPasswordError() ? (
                      <span className="field-success-text">✓ Passwords match</span>
                    ) : null}
                  </div>

                  <button type="submit" className="regbtn-primary" disabled={loading}>
                    {loading ? 'Sending Code...' : 'Continue'}
                  </button>
                </form>
              ) : (
                // OTP Verification Form
                <form onSubmit={handleVerifyOTP}>
                  <div className="otp-sent-info">
                    <p>📧 Verification code sent to:</p>
                    <strong>{formData.email}</strong>

                    {window.location.hostname === 'localhost' && (
                      <div className="dev-otp-hint" style={{
                        // marginTop: '1rem',
                        // padding: '10px',
                        // background: '#fff3cd',
                        // border: '1px solid #ffeeba',
                        // borderRadius: '8px',
                        // fontSize: '0.85rem',
                        // color: '#856404'
                      }}>
                        {/* <strong>Developer Hint:</strong> Check your <b>Backend Terminal</b> for the verification code. */}
                      </div>
                    )}
                  </div>

                  <label>Enter Verification Code</label>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    required
                    placeholder="123456"
                    maxLength={6}
                    className="otp-input-field"
                  />

                  <button type="submit" className="regbtn-primary" disabled={loading}>
                    {loading ? 'Verifying...' : 'Verify Email'}
                  </button>

                  <button
                    type="button"
                    className="resend-link"
                    onClick={handleRequestOTP}
                    disabled={loading}
                  >
                    Didn't receive code? Resend
                  </button>
                </form>
              )}

              <p className="footer-text">Already have an account? <Link to="/login">Sign in</Link></p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- STEP 2: Select Your Role ---
  if (step === 'selection') {
    return (
      <div className="role-hero-wrapper">
        {/* Layer 1: Background Image (Visible & Blurry) */}
        <div className="role-hero-bg" style={{ backgroundImage: `url(${roleBg})` }}></div>

        {/* Layer 2: White Premium Overlay */}
        <div className="role-hero-overlay"></div>

        {/* Layer 3: Content */}
        <div className="role-hero-content">
          <div className="role-hero-header">
            <h1 className="role-hero-title">Select Your Role</h1>
            <p className="role-hero-subtitle">
              Join the future of industry-integrated education.
            </p>
          </div>

          <div className="role-cards-grid">

            {/* Student Card (Hidden)
            <div className="role-card-v2 role-card-anim-1" onClick={() => { setSelectedRole('student'); setStep('profile'); }}>
              <div className="role-card-v2-icon student-icon-v2">
                <GraduationCap size={40} strokeWidth={1.5} />
              </div>
              <h3 className="role-card-v2-title">Student / Learner</h3>
              <p className="role-card-v2-desc">Access industry-aligned courses, mentorship, and career opportunities</p>
              <ul className="role-card-v2-list">
                <li><span className="bullet-dot"></span> Discover expert courses</li>
                <li><span className="bullet-dot"></span> Connect with industry mentors</li>
                <li><span className="bullet-dot"></span> Cross-domain exploration</li>
              </ul>
              <button className="role-card-v2-btn student-btn-v2" onClick={(e) => { e.stopPropagation(); setSelectedRole('student'); setStep('profile'); }}>
                Continue as Student <span className="btn-arrow">→</span>
              </button>
            </div>
            */}

            {/* Institution Admin Card */}
            <div className="role-card-v2 role-card-anim-2" onClick={() => { setSelectedRole('university'); setStep('profile'); }}>
              <div className="role-card-v2-icon admin-icon-v2">
                <Building2 size={40} strokeWidth={1.5} />
              </div>
              <h3 className="role-card-v2-title">Institution Admin</h3>
              <p className="role-card-v2-desc">Connect your institution with vetted industry professionals</p>
              <ul className="role-card-v2-list">
                <li><span className="bullet-dot"></span> Browse vetted experts</li>
                <li><span className="bullet-dot"></span> Manage engagements</li>
                <li><span className="bullet-dot"></span> NEP Aligned Framework</li>
              </ul>
              <button className="role-card-v2-btn admin-btn-v2" onClick={(e) => { e.stopPropagation(); setSelectedRole('university'); setStep('profile'); }}>
                Continue as Institution Admin <span className="btn-arrow">→</span>
              </button>
            </div>

            {/* Professor of Practice Card */}
            <div className="role-card-v2 role-card-anim-3" onClick={() => { setSelectedRole('pop'); setStep('profile'); }}>
              <div className="role-card-v2-icon pop-icon-v2">
                <Briefcase size={40} strokeWidth={1.5} />
              </div>
              <h3 className="role-card-v2-title">Professor of Practice</h3>
              <p className="role-card-v2-desc">Share your expertise and mentor the next generation</p>
              <ul className="role-card-v2-list">
                <li><span className="bullet-dot"></span> Set your schedule</li>
                <li><span className="bullet-dot"></span> Flexible engagements</li>
                <li><span className="bullet-dot"></span> Build Academic Career</li>
              </ul>
              <button className="role-card-v2-btn pop-btn-v2" onClick={(e) => { e.stopPropagation(); setSelectedRole('pop'); setStep('profile'); }}>
                Continue as Professor <span className="btn-arrow">→</span>
              </button>
            </div>
          </div>

          <p className="role-hero-help">
            Not sure which role to choose?{' '}
            <button className="learn-more-btn-v2" onClick={() => setShowRoleDetails(!showRoleDetails)}>
              {showRoleDetails ? 'Hide details' : 'Learn more about each role'}
            </button>
          </p>

          {showRoleDetails && (
            <div className="role-details-v2">


              <div className="role-detail-v2-item">
                <div className="detail-v2-header">
                  <span className="detail-v2-icon"><Briefcase size={24} /></span>
                  <h3>Professor of Practice</h3>
                </div>
                <p>For experienced professionals who want to give back to academia by teaching, mentoring, or advising.</p>
                <ul className="detail-v2-list">
                  <li><strong>Flexible Engagement:</strong> Choose from guest lectures, adjunct roles, or mentorship sessions that fit your schedule.</li>
                  <li><strong>Monetize Expertise:</strong> Earn for your time and knowledge through secure, transparent payment channels.</li>
                  <li><strong>Build Legacy:</strong> Shape the next generation of talent and influence academic curricula.</li>
                </ul>
              </div>

              <div className="role-detail-v2-item">
                <div className="detail-v2-header">
                  <span className="detail-v2-icon"><Landmark size={24} /></span>
                  <h3>Institution Admin</h3>
                </div>
                <p>For academic leaders looking to bridge the gap between industry and academia.</p>
                <ul className="detail-v2-list">
                  <li><strong>Vetted Experts:</strong> Access a pool of pre-verified professionals for guest lectures and advisory boards.</li>
                  <li><strong>Curriculum Enhancement:</strong> Collaborate with industry experts to ensure your courses meet market demands.</li>
                  <li><strong>Streamlined Management:</strong> Handle contracts, scheduling, and compliance all in one secure platform.</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }


  // --- STEP 3: Complete Profile ---
  return (
    <div className="register-wrapper">
      <div className="register-split">
        <div className="register-image-side">
          <img src={registerImg} alt="Join EdVergenceX" />
          <div className="image-overlay">
            <h2>Complete Profile</h2>
            <p>Tell us a bit more about yourself so we can tailor the perfect environment for you.</p>
          </div>
        </div>
        <div className="register-form-side profile-side">
          <div className="register-card">
            <button className="regback-btn" onClick={() => setStep('selection')}>← Back to Roles</button>
            <h2>Complete Your {selectedRole === 'pop' ? 'Professor' : selectedRole === 'university' ? 'Institution' : 'Student'} Profile</h2>

            {error && <div className="error-message">{error}</div>}

            <form onSubmit={handleFinalSubmit}>
              {selectedRole === 'student' && (
                <>
                  <label>Full Name</label>
                  <input name="full_name" onChange={handleChange} required placeholder="Enter your full name" maxLength={50} />
                  <label>Phone Number</label>
                  <div className="phone-input-container">
                    <PhoneInput
                      country={'in'}
                      value={formData.phone_number}
                      onChange={handlePhoneChange}
                      enableSearch={true}
                      disableSearchIcon={true}
                      placeholder="Enter phone number"
                      inputProps={{
                        name: 'phone_number',
                        required: true,
                        autoFocus: false
                      }}
                      containerClass="react-tel-input"
                      inputClass="phone-form-control"
                      buttonClass="phone-dropdown-btn"
                      limitMaxLength={true}
                    />
                  </div>
                  <label>Current Institution</label>
                  <select
                    name="student_institution"
                    value={formData.student_institution}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select your university...</option>
                    {universities.map((uni, idx) => (
                      <option key={idx} value={uni}>{uni}</option>
                    ))}
                  </select>
                  {/* Domain Selection */}
                  <div style={{ margin: '2rem 0' }}>
                    <DomainSelector
                      selectedDomains={formData.preferred_domains}
                      onDomainsChange={handleDomainsChange}
                      maxSelection={3}
                    />
                  </div>

                  <label>Key Skills</label>
                  <SkillsInput selectedSkills={formData.skills} onSkillsChange={handleSkillsChange} />
                </>
              )}

              {selectedRole === 'pop' && (
                <>
                  <label>Full Name</label>
                  <div className="honorific-input-group" style={{ marginBottom: '1.25rem' }}>
                    <select
                      name="honorific"
                      value={formData.honorific || 'Dr.'}
                      onChange={handleChange}
                      className="honorific-select"
                    >
                      <option value="Dr.">Dr.</option>
                      <option value="Mr.">Mr.</option>
                      <option value="Ms.">Ms.</option>
                      <option value="Mrs.">Mrs.</option>
                      <option value="Other">Other</option>
                    </select>
                    {formData.honorific === 'Other' && (
                      <input
                        name="custom_honorific"
                        value={formData.custom_honorific || ''}
                        onChange={handleChange}
                        placeholder="e.g. Prof."
                        className="custom-honorific-input"
                        maxLength={15}
                        required
                      />
                    )}
                    <input
                      name="full_name"
                      value={formData.full_name || ''}
                      onChange={handleChange}
                      placeholder="Full Name"
                      className="name-input"
                      required
                      maxLength={50}
                    />
                  </div>
                  <label>Phone Number</label>
                  <div className="phone-input-container">
                    <PhoneInput
                      country={'in'}
                      value={formData.phone_number}
                      onChange={handlePhoneChange}
                      enableSearch={true}
                      disableSearchIcon={true}
                      placeholder="Enter phone number"
                      inputProps={{
                        name: 'phone_number',
                        required: true,
                        autoFocus: false
                      }}
                      containerClass="react-tel-input"
                      inputClass="phone-form-control"
                      buttonClass="phone-dropdown-btn"
                      limitMaxLength={true}
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Job Title</label>
                      <input name="job_title" onChange={handleChange} placeholder="e.g. Software Developer" required maxLength={50} />
                    </div>
                    <div className="form-group">
                      <label>Current Company</label>
                      <input name="company" onChange={handleChange} placeholder="Current Company" required maxLength={50} />
                    </div>
                  </div>
                  <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                    <Autocomplete
                      label="Location"
                      name="location"
                      value={formData.location}
                      options={ALL_LOCATIONS}
                      onChange={handleChange}
                      required
                      placeholder="Search city or state..."
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Primary Domain</label>
                      <select name="domain" onChange={handleChange} required>
                        {INDUSTRY_DOMAINS.map(d => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Background Category</label>
                      <select
                        name="background_category"
                        value={formData.background_category}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select Background Category</option>
                        {POP_BACKGROUND_CATEGORIES.map(c => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* EXPERTISE TAXONOMY */}
                  <div className="form-row">
                    <div className="form-group">
                      <label>Expertise Category</label>
                      <select
                        name="expertise_category"
                        value={formData.expertise_category}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            expertise_category: e.target.value,
                            expertise_subcategory: '',
                            expertise_core: ''
                          });
                        }}
                        required
                      >
                        <option value="">Select Category...</option>
                        {dynIndustries.length > 0
                          ? dynIndustries.map(ind => (
                              <option key={ind.id} value={ind.name}>{ind.name}</option>
                            ))
                          : Object.keys(EXPERTISE_TAXONOMY).map(cat => (
                              <option key={cat} value={cat}>{cat}</option>
                            ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Expertise Sub-category</label>
                      <select
                        name="expertise_subcategory"
                        value={formData.expertise_subcategory}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            expertise_subcategory: e.target.value,
                            expertise_core: ''
                          });
                        }}
                        required
                        disabled={!formData.expertise_category}
                      >
                        <option value="">Select Sub-category...</option>
                        {dynCategories.length > 0
                          ? dynCategories.map(cat => (
                              <option key={cat.id} value={cat.name}>{cat.name}</option>
                            ))
                          : formData.expertise_category && EXPERTISE_TAXONOMY[formData.expertise_category]
                            ? Object.keys(EXPERTISE_TAXONOMY[formData.expertise_category]).map(sub => (
                                <option key={sub} value={sub}>{sub}</option>
                              ))
                            : null}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Core Expertise</label>
                      <select
                        name="expertise_core"
                        value={formData.expertise_core}
                        onChange={handleChange}
                        required
                        disabled={!formData.expertise_subcategory}
                      >
                        <option value="">Select Core Expertise...</option>
                        {dynSubcategories.length > 0
                          ? dynSubcategories.map(sub => (
                              <option key={sub.id} value={sub.name}>{sub.name}</option>
                            ))
                          : formData.expertise_category && formData.expertise_subcategory && EXPERTISE_TAXONOMY[formData.expertise_category]?.[formData.expertise_subcategory]
                            ? EXPERTISE_TAXONOMY[formData.expertise_category][formData.expertise_subcategory].map(core => (
                                <option key={core} value={core}>{core}</option>
                              ))
                            : null}
                      </select>
                    </div>
                  </div>
                  {/* END EXPERTISE TAXONOMY */}

                  <div className="form-row">
                    <div className="form-group">
                      <label>Years of Experience</label>
                      <input
                        type="number"
                        name="years_of_experience"
                        value={formData.years_of_experience !== undefined ? formData.years_of_experience : ''}
                        onChange={handleChange}
                        onKeyDown={(e) => {
                          if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                            e.preventDefault();
                          }
                        }}
                        placeholder="e.g. 5"
                        min="0"
                        max="70"
                        required
                      />
                    </div>
                  </div>

                  <div className="education-section" style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', marginBottom: '1rem' }}>
                    <Autocomplete
                      label="Highest Education"
                      name="highest_education"
                      value={formData.highest_education}
                      options={EDUCATION_OPTIONS}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData(prev => ({
                          ...prev,
                          highest_education: val,
                          // Reset child fields if parent changes
                          custom_education: val === 'Others' ? prev.custom_education : '',
                          education_specialization: TECHNICAL_DEGREES.includes(val) ? prev.education_specialization : '',
                          custom_specialization: ''
                        }));
                      }}
                      required
                      creatable={true}
                      placeholder="Select or type your education..."
                    />

                    {formData.highest_education === 'Others' && (
                      <div className="form-group animate-slide-down">
                        <label>Please specify your education <span style={{ color: '#ef4444' }}>*</span></label>
                        <input
                          name="custom_education"
                          value={formData.custom_education}
                          onChange={handleChange}
                          placeholder="e.g. Master of Fine Arts"
                          required
                        />
                      </div>
                    )}

                    {TECHNICAL_DEGREES.includes(formData.highest_education) && (
                      <div className="form-group animate-slide-down">
                        <Autocomplete
                          label="Branch / Specialization"
                          name="education_specialization"
                          value={formData.education_specialization}
                          options={BRANCH_OPTIONS}
                          onChange={(e) => {
                            const val = e.target.value;
                            setFormData(prev => ({
                              ...prev,
                              education_specialization: val,
                              custom_specialization: val === 'Others' ? prev.custom_specialization : ''
                            }));
                          }}
                          required
                          creatable={true}
                          placeholder="e.g. Computer Science, Finance..."
                        />
                        <small style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: '-12px' }}>
                          For example: CSE, AI & ML, Finance, Marketing, etc.
                        </small>
                      </div>
                    )}

                    {formData.education_specialization === 'Others' && (
                      <div className="form-group animate-slide-down">
                        <label>Please specify your branch <span style={{ color: '#ef4444' }}>*</span></label>
                        <input
                          name="custom_specialization"
                          value={formData.custom_specialization}
                          onChange={handleChange}
                          placeholder="e.g. Quantum Computing"
                          required
                        />
                      </div>
                    )}
                  </div>

                  <label>Expertise Areas</label>
                  <div className="common-skills-container">
                    <p className="common-skills-label">Suggested:</p>
                    <div className="common-skills-tags">
                      {(DOMAIN_SKILLS[formData.domain] || ['Leadership', 'Project Management', 'Agile Methodologies', 'Public Speaking', 'Data Analysis', 'Software Development', 'Marketing Strategy', 'Business Strategy', 'Product Management', 'Mentoring']).map(skill => (
                        <button
                          key={skill}
                          type="button"
                          className={`skill-tag-suggestion ${formData.skills.includes(skill) ? 'selected' : ''}`}
                          onClick={() => {
                            if (formData.skills.includes(skill)) {
                              handleSkillsChange(formData.skills.filter(s => s !== skill));
                            } else {
                              handleSkillsChange([...formData.skills, skill]);
                            }
                          }}
                        >
                          {formData.skills.includes(skill) ? '✓ ' : '+ '}{skill}
                        </button>
                      ))}
                    </div>
                  </div>
                  <SkillsInput selectedSkills={formData.skills} onSkillsChange={handleSkillsChange} />
                  <div className="form-row">
                    <div className="form-group">
                      <label>LinkedIn URL</label>
                      <input name="linkedin_url" onChange={handleChange} placeholder="https://linkedin.com/in/..." />
                    </div>
                    <div className="form-group">
                      <label>Resume / Portfolio Link</label>
                      <input name="resume_link" onChange={handleChange} placeholder="https://..." />
                    </div>
                  </div>
                </>
              )}

              {selectedRole === 'university' && (
                <>
                  <label>Organization Name</label>
                  <input name="organization_name" onChange={handleChange} required maxLength={100} />
                  <label>Phone Number</label>
                  <div className="phone-input-container">
                    <PhoneInput
                      country={'in'}
                      value={formData.phone_number}
                      onChange={handlePhoneChange}
                      enableSearch={true}
                      disableSearchIcon={true}
                      placeholder="Enter phone number"
                      inputProps={{
                        name: 'phone_number',
                        required: true,
                        autoFocus: false
                      }}
                      containerClass="react-tel-input"
                      inputClass="phone-form-control"
                      buttonClass="phone-dropdown-btn"
                      limitMaxLength={true}
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Institution Type</label>
                      <select name="institution_type" onChange={handleChange} required>
                        <option value="">Select Type...</option>
                        <option value="University">University</option>
                        <option value="Autonomous">Autonomous</option>
                        <option value="VTU">VTU</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Contact Person / Dean Name</label>
                      <div className="honorific-input-group">
                        <select
                          name="contact_honorific"
                          value={formData.contact_honorific || 'Dr.'}
                          onChange={handleChange}
                          className="honorific-select"
                        >
                          <option value="Dr.">Dr.</option>
                          <option value="Mr.">Mr.</option>
                          <option value="Ms.">Ms.</option>
                          <option value="Mrs.">Mrs.</option>
                          <option value="Other">Other</option>
                        </select>
                        {formData.contact_honorific === 'Other' && (
                          <input
                            name="custom_contact_honorific"
                            value={formData.custom_contact_honorific || ''}
                            onChange={handleChange}
                            placeholder="e.g. Prof."
                            className="custom-honorific-input"
                            maxLength={15}
                            required
                          />
                        )}
                        <input
                          name="contact_person"
                          value={formData.contact_person || ''}
                          onChange={handleChange}
                          placeholder="Contact person / Dean name"
                          className="name-input"
                          required
                          maxLength={50}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="form-group" style={{ marginBottom: '1rem' }}>
                    <label>University Tier</label>
                    <div style={{ display: 'flex', gap: '20px', marginTop: '5px' }}>
                    {['Tier 1', 'Tier 2', 'Tier 3'].map((t) => (
                      <label key={t} style={{ display: 'flex', alignItems: 'center', fontWeight: '700', cursor: 'pointer', color: 'var(--text-muted)' }}>
                        <input
                          type="radio"
                          name="tier"
                          value={t}
                          checked={formData.tier === t}
                          onChange={handleChange}
                          style={{ marginRight: '8px', width: 'auto' }}
                        />
                        {t}
                      </label>
                    ))}
                  </div>
                  <small style={{ color: 'var(--text-faint)', fontSize: '0.85rem' }}>
                    Select your institution tier for standardized rate cards.
                  </small>
                </div>

                  <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                    <Autocomplete
                      label="Location (City or State)"
                      name="city"
                      value={formData.city}
                      options={ALL_LOCATIONS}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData(prev => ({
                          ...prev,
                          city: val,
                          state: val,
                          country: 'India'
                        }));
                      }}
                      required
                      placeholder="Search city or state..."
                    />
                  </div>
                </>
              )}

              <button type="submit" className="regbtn-primary">Finish & Create Account</button>
            </form>
          </div>
        </div>
      </div>

      {/* Toast Notifications */}
      <div style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
      }}>
        {toasts.map(toast => (
          <div
            key={toast.id}
            style={{
              background: toast.type === 'success' ? '#10b981' : '#ef4444',
              color: '#fff',
              padding: '16px 24px',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              minWidth: '300px',
              maxWidth: '500px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              animation: 'slideIn 0.3s ease-out',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            <span style={{ fontSize: '20px' }}>
              {toast.type === 'success' ? '\u2713' : '\u2715'}
            </span>
            <span style={{ flex: 1 }}>{toast.message}</span>
            <button
              onClick={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#fff',
                cursor: 'pointer',
                fontSize: '18px',
                padding: '0',
                opacity: 0.8,
                transition: 'opacity 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.opacity = '1'}
              onMouseLeave={(e) => e.target.style.opacity = '0.8'}
            >
              ×
            </button>
          </div>
        ))}
      </div>

      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(400px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}

export default Register;
